import { DataSeries } from '@next/core-lib/models';
import { Action, createReducer, on } from '@ngrx/store';

import {
  availableKpiParameters,
  ObservationsRegistration,
  PerformanceModuleState,
  WeeklyKpiDataResult,
} from '../../models/performance.model';
import * as performanceActions from '../actions/performance.actions';

export const initialState = <PerformanceModuleState>{
  observations: undefined,
  selectedSectionIds: [],
  selectedKpiParameters: availableKpiParameters,
  selectedWeekNr: undefined,
  selectedCycleId: undefined,
  weeklyKpiDataResults: [],
  dailyKpiTimeSeries: undefined,
};

const performanceReducer = createReducer(
  initialState,
  on(performanceActions.setSelectedCycleId, (state, { cycleId }) => ({
    ...state,
    selectedCycleId: cycleId,
    selectedWeekNr: undefined as number,
    selectedSectionIds: [] as number[],
    observations: undefined as ObservationsRegistration[],
  })),
  on(performanceActions.setSelectedWeekNr, (state, { weekNr }) => ({
    ...state,
    selectedWeekNr: weekNr,
  })),
  on(performanceActions.setSelectedSectionIds, (state, { sectionIds }) => ({
    ...state,
    selectedSectionIds: sectionIds,
  })),
  on(performanceActions.setSelectedKpiParameters, (state, { kpiParameters }) => ({
    ...state,
    selectedKpiParameters: kpiParameters,
  })),
  on(performanceActions.loadObservationsForCycleSuccess, (state, { observations }) => ({
    ...state,
    observations: [...observations].sort((o) => o.day),
    selectedWeekNr: getLatestWeek(observations),
  })),
  on(performanceActions.loadObservationsForCycleFailed, (state, _) => ({
    ...state,
    observations: [] as ObservationsRegistration[],
  })),
  on(performanceActions.upsertObservationsForCycleSuccess, (state, { observations }) => ({
    ...state,
    observations: [
      ...state.observations.filter(
        (so) =>
          observations.findIndex(
            (o) => o.day === so.day && o.sectionId === so.sectionId && o.cycleId === so.cycleId,
          ) < 0,
      ),
    ]
      .concat(observations)
      .sort((o) => o.day),
    selectedWeekNr: getLatestWeek(observations),
  })),
  on(performanceActions.loadWeeklyKpiResultsSuccess, (state, { results }) => ({
    ...state,
    weeklyKpiDataResults: results,
  })),
  on(performanceActions.loadWeeklyKpiResultsFailed, (state, _) => ({
    ...state,
    weeklyKpiDataResults: [] as WeeklyKpiDataResult[],
  })),
  on(performanceActions.loadDailyKpiTimeSeriesSuccess, (state, { timeSeries: timeseries }) => ({
    ...state,
    dailyKpiTimeSeries: timeseries,
  })),
  on(performanceActions.loadDailyKpiTimeSeriesFailed, (state, _) => ({
    ...state,
    dailyKpiTimeSeries: undefined as DataSeries<number, number>,
  })),
);

export function getLatestWeek(observations: ObservationsRegistration[]) {
  return observations.length > 0
    ? Math.floor(Math.max(...observations.map((o) => o.day)) / 7) + 1
    : 0;
}

export function reducer(state: PerformanceModuleState | undefined, action: Action) {
  return performanceReducer(state, action);
}
